
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Site } from "@/services/types";
import { MapPin, Phone, Mail, Clock, Globe, Star, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { getSiteUrl } from "@/utils/siteUrlUtils";
import GoogleRating from "./GoogleRating";

interface SiteCardProps {
  site: Site;
}

const SiteCard: React.FC<SiteCardProps> = ({ site }) => {
  const getDirectionsUrl = (site: Site) => {
    const query = encodeURIComponent(`${site.address}, ${site.postalCode} ${site.city}`);
    return `https://www.google.com/maps/dir/?api=1&destination=${query}`;
  };

  const getSitePath = (site: Site) => {
    return getSiteUrl(site.id, site.name, site.city);
  };

  const formatOpeningHours = (site: Site) => {
    if (!site.opening_hours) return "Horaires disponibles sur demande";
    
    const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long' }).toLowerCase();
    const dayMapping: { [key: string]: string } = {
      'lundi': 'lundi',
      'mardi': 'mardi',
      'mercredi': 'mercredi',
      'jeudi': 'jeudi',
      'vendredi': 'vendredi',
      'samedi': 'samedi',
      'dimanche': 'dimanche'
    };
    
    const todayFrench = dayMapping[today] || 'lundi';
    const todayHours = site.opening_hours[todayFrench as keyof typeof site.opening_hours];
    
    if (!todayHours) return "Horaires disponibles sur demande";
    
    if (typeof todayHours === 'object' && todayHours.matin && todayHours.apres_midi) {
      if (todayHours.matin === 'Fermé' && todayHours.apres_midi === 'Fermé') {
        return "Fermé aujourd'hui";
      }
      if (todayHours.matin !== 'Fermé' && todayHours.apres_midi !== 'Fermé') {
        return `Aujourd'hui: ${todayHours.matin}, ${todayHours.apres_midi}`;
      }
      if (todayHours.matin !== 'Fermé') {
        return `Aujourd'hui: ${todayHours.matin}`;
      }
      if (todayHours.apres_midi !== 'Fermé') {
        return `Aujourd'hui: ${todayHours.apres_midi}`;
      }
    }
    
    return "Horaires disponibles sur demande";
  };

  return (
    <Card className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 bg-white border-gray-100 overflow-hidden">
      {/* Header avec dégradé */}
      <div className="bg-gradient-to-r from-fabre-green via-fabre-teal to-fabre-green p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/5 backdrop-blur-sm"></div>
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-fabre-lightgreen transition-colors">
                {site.name}
              </h3>
              <div className="flex items-center text-white/90 mb-3">
                <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                <div className="text-sm">
                  <p>{site.address}</p>
                  <p className="font-medium">{site.postalCode} {site.city}</p>
                </div>
              </div>
            </div>
            <div className="bg-white/20 rounded-full p-2">
              <MapPin className="h-6 w-6 text-white" />
            </div>
          </div>
          
          {/* Services badges */}
          <div className="flex flex-wrap gap-2">
            {site.services?.slice(0, 3).map((service, index) => (
              <Badge 
                key={index} 
                variant="secondary" 
                className="bg-white/20 text-white border-white/30 text-xs"
              >
                {service}
              </Badge>
            ))}
            {site.services && site.services.length > 3 && (
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 text-xs">
                +{site.services.length - 3} autres
              </Badge>
            )}
          </div>
        </div>
      </div>

      <CardContent className="p-6 space-y-4 bg-gradient-to-br from-fabre-green/5 to-fabre-green/15">
        {/* Informations de contact */}
        <div className="space-y-3">
          {site.phone && (
            <div className="flex items-center text-gray-600 hover:text-fabre-green transition-colors">
              <Phone className="h-4 w-4 mr-3 text-fabre-green flex-shrink-0" />
              <a 
                href={`tel:${site.phone}`}
                className="font-medium hover:underline"
              >
                {site.phone}
              </a>
            </div>
          )}
          
          {site.email && (
            <div className="flex items-center text-gray-600 hover:text-fabre-green transition-colors">
              <Mail className="h-4 w-4 mr-3 text-fabre-green flex-shrink-0" />
              <a 
                href={`mailto:${site.email}`}
                className="font-medium hover:underline text-sm"
              >
                {site.email}
              </a>
            </div>
          )}
          
          <div className="flex items-start text-gray-600">
            <Clock className="h-4 w-4 mr-3 text-fabre-green flex-shrink-0 mt-0.5" />
            <span className="text-sm font-medium">
              {formatOpeningHours(site)}
            </span>
          </div>
        </div>

        {/* Notes Google */}
        <div className="bg-gradient-to-r from-yellow-50/80 to-orange-50/80 rounded-lg p-4">
          <GoogleRating 
            siteName={site.name}
            address={site.address}
            city={site.city}
          />
        </div>

        {/* Description */}
        {site.description && (
          <div className="bg-fabre-lightgray/20 rounded-lg p-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              {site.description.length > 150 
                ? `${site.description.substring(0, 150)}...` 
                : site.description
              }
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
          <Button
            asChild
            className="flex-1 bg-fabre-green hover:bg-fabre-darkgreen text-white"
          >
            <Link to={getSitePath(site)} className="flex items-center justify-center">
              <span>Voir le site</span>
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          
          <div className="flex gap-2">
            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-fabre-green text-fabre-green hover:bg-fabre-lightgreen"
            >
              <a 
                href={getDirectionsUrl(site)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center"
              >
                <MapPin className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Itinéraire</span>
              </a>
            </Button>
            
            {site.websiteUrl && (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-fabre-teal text-fabre-teal hover:bg-fabre-lightgreen"
              >
                <a 
                  href={site.websiteUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center"
                >
                  <Globe className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Site web</span>
                </a>
              </Button>
            )}
            
            {site.reviewUrl && (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-fabre-gold text-fabre-gold hover:bg-fabre-lightgreen"
              >
                <a 
                  href={site.reviewUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center"
                >
                  <Star className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Avis</span>
                </a>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SiteCard;
